import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";
import { useApp } from "@/context/AppContext";
import EarningModels from "@/components/EarningModels";
import { PenaltySettings, TdsSettings } from "@/components/TaxSettings";
import CertificateSettings from "@/components/certs/CertificateSettings";
import { RainSurchargeSettings } from "@/components/RainSurchargeSettings";

export default function Settings() {
  const { role, can } = useApp();
  const showModels = can("set.view_models");
  const showGeneral = can("mod.settings");
  const showRates = can("mod.settings") || can("set.multi_rate");
  const showFr = can("fr.commission_view") || can("fr.commission_edit") || can("set.fr_commission");
  const showTax = can("pen.settings") || can("tax.settings");
  const [settings, setSettings] = useState(null);
  const [slabs, setSlabs] = useState([]);
  const [fslabs, setFslabs] = useState([]);
  const [open, setOpen] = useState(false);
  const [nf, setNf] = useState({});

  const load = () => {
    api.get("/settings").then(({ data }) => setSettings(data));
    api.get("/slabs").then(({ data }) => setSlabs(data));
    api.get("/franchise-slabs").then(({ data }) => setFslabs(data));
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      const { rain_enabled, rain_amount, rain_max_orders, ...rest } = settings;
      await api.put("/settings", { ...rest, _user: role }); toast.success("Settings saved");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveSlab = async (s) => {
    try { await api.put(`/slabs/${s.id}`, { rate: Number(s.rate), min_orders: Number(s.min_orders), max_orders: s.max_orders === null || s.max_orders === "" ? null : Number(s.max_orders), _user: role }); toast.success("Slab updated"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const addSlab = async () => {
    try {
      await api.post("/slabs", { label: nf.label, min_orders: Number(nf.min_orders), max_orders: nf.max_orders ? Number(nf.max_orders) : null, rate: Number(nf.rate), platform: nf.platform || "All", location: "All", category: "Standard", _user: role });
      toast.success("Slab created"); setOpen(false); setNf({}); load();
    } catch (e) { toast.error("Failed"); }
  };

  if (!settings) return null;

  return (
    <div>
      <PageHeader title="Admin Settings" subtitle="Configure slabs, rates & business rules — nothing is hard-coded" />
      <Tabs defaultValue={showGeneral ? "slabs" : showModels ? "models" : showRates ? "rates" : showFr ? "franchise" : can("cert.branding") && !can("pen.settings") && !can("daily.penalty") ? "certificates" : "penalties"}>
        <TabsList>
          {showModels && <TabsTrigger value="models" data-testid="settings-tab-models">Earning Models</TabsTrigger>}
          {showGeneral && <TabsTrigger value="slabs" data-testid="settings-tab-slabs">Earning Slabs</TabsTrigger>}
          {showRates && <TabsTrigger value="rates" data-testid="settings-tab-rates">Rates & Charges</TabsTrigger>}
          {(showRates || can("set.rain")) && <TabsTrigger value="rain" data-testid="settings-tab-rain">Rain Surcharge</TabsTrigger>}
          {showGeneral && <TabsTrigger value="ev" data-testid="settings-tab-ev">EV Plans</TabsTrigger>}
          {showFr && <TabsTrigger value="franchise" data-testid="settings-tab-franchise">Franchise Slabs</TabsTrigger>}
          {(can("pen.settings") || can("daily.penalty")) && <TabsTrigger value="penalties" data-testid="settings-tab-penalties">Order Penalties</TabsTrigger>}
          {(can("tax.settings") || can("tax.view")) && <TabsTrigger value="tds" data-testid="settings-tab-tds">Tax & TDS</TabsTrigger>}
          {can("cert.branding") && <TabsTrigger value="certificates" data-testid="settings-tab-certificates">Certificate Management</TabsTrigger>}
        </TabsList>
        <TabsContent value="certificates" className="mt-4"><CertificateSettings /></TabsContent>
        <TabsContent value="penalties" className="mt-4"><PenaltySettings /></TabsContent>
        <TabsContent value="tds" className="mt-4"><TdsSettings /></TabsContent>

        <TabsContent value="models" className="mt-4">
          <EarningModels />
        </TabsContent>

        <TabsContent value="slabs" className="mt-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Label className="mb-0">Calculation Method</Label>
              <Select value={settings.slab_method} onValueChange={(v) => { setSettings({ ...settings, slab_method: v }); }}>
                <SelectTrigger className="w-56" data-testid="slab-method-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="total">Total Orders × Applicable Rate</SelectItem><SelectItem value="progressive">Progressive Slab</SelectItem></SelectContent>
              </Select>
              <Button size="sm" onClick={saveSettings} data-testid="save-method-btn"><Save className="mr-1 h-4 w-4" />Save</Button>
            </div>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-slab-btn"><Plus className="mr-1 h-4 w-4" /> Add Slab</Button>
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Label</TableHead><TableHead>Min Orders</TableHead><TableHead>Max Orders</TableHead><TableHead>Rate (₹)</TableHead><TableHead>Platform</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {slabs.filter((s) => s.active !== false).map((s, i) => (
                  <TableRow key={s.id} data-testid={`slab-row-${i}`}>
                    <TableCell className="font-semibold">{s.label}</TableCell>
                    <TableCell><Input className="h-8 w-20" defaultValue={s.min_orders} onChange={(e) => (s.min_orders = e.target.value)} /></TableCell>
                    <TableCell><Input className="h-8 w-20" defaultValue={s.max_orders ?? ""} placeholder="∞" onChange={(e) => (s.max_orders = e.target.value)} /></TableCell>
                    <TableCell><Input className="h-8 w-24" defaultValue={s.rate} onChange={(e) => (s.rate = e.target.value)} data-testid={`slab-rate-${i}`} /></TableCell>
                    <TableCell>{s.platform}</TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => saveSlab(s)} data-testid={`save-slab-${i}`}>Save</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="rain" className="mt-4"><RainSurchargeSettings canEdit={can("mod.settings") || can("set.rain")} /></TabsContent>
        <TabsContent value="rates" className="mt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-2xl">
            <div className="rounded-xl border border-border bg-card p-4"><Label>Extra Distance Rate (₹/KM)</Label><Input type="number" value={settings.extra_km_rate} onChange={(e) => setSettings({ ...settings, extra_km_rate: Number(e.target.value) })} data-testid="extra-km-rate" /></div>
            <div className="rounded-xl border border-border bg-card p-4 sm:col-span-2" data-testid="multi-order-settings">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">Multi-Order Rate (Global Default)</p><p className="text-xs text-muted-foreground">Used for DPs on global slabs, and for earning models that enable multi orders without their own rate. When off, multi orders are paid at the normal rate.</p></div>
                <Switch checked={!!settings.multi_order_enabled} onCheckedChange={(v) => setSettings({ ...settings, multi_order_enabled: v })} data-testid="multi-order-enabled-switch" />
              </div>
              <Label className="mt-3 block">Default Rate per Multi Order (₹)</Label>
              <Input type="number" min="0" className="max-w-xs" value={settings.default_multi_order_rate ?? ""} onChange={(e) => setSettings({ ...settings, default_multi_order_rate: e.target.value === "" ? null : Number(e.target.value) })} placeholder="e.g. 30" data-testid="default-multi-order-rate" />
            </div>
            <div className="rounded-xl border border-border bg-card p-4"><Label>Security Deposit Default (₹)</Label><Input type="number" value={settings.security_deposit_default} onChange={(e) => setSettings({ ...settings, security_deposit_default: Number(e.target.value) })} /></div>
            <div className="rounded-xl border border-border bg-card p-4"><Label>Payout Cycle</Label><Select value={settings.payout_cycle} onValueChange={(v) => setSettings({ ...settings, payout_cycle: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Daily", "Weekly", "Monthly"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <Button className="mt-4" onClick={saveSettings} data-testid="save-rates-btn"><Save className="mr-1 h-4 w-4" /> Save Rates</Button>
        </TabsContent>

        <TabsContent value="ev" className="mt-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {(settings.ev_plans || []).map((p) => (
              <div key={p.id} className="rounded-xl border border-border bg-card p-4"><h3 className="font-head font-semibold">{p.name}</h3><p className="mt-2 font-head text-2xl font-bold text-cyan-500">{inr(p.amount)}</p><p className="text-xs text-muted-foreground">per {p.frequency}</p></div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="franchise" className="mt-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Franchise Partner earning per completed order — independent of DP earnings.</p>
            <Button size="sm" variant="outline" onClick={async () => { try { await api.post("/franchise-slabs", { label: "New Slab", min_orders: 0, max_orders: null, rate: 0 }); load(); } catch (e) { toast.error("Failed"); } }} data-testid="add-fslab-btn"><Plus className="mr-1 h-4 w-4" /> Add Slab</Button>
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Label</TableHead><TableHead>Min Orders</TableHead><TableHead>Max Orders</TableHead><TableHead>Rate (₹/order)</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {fslabs.filter((s) => s.active !== false).map((s, i) => (
                  <TableRow key={s.id} data-testid={`fslab-row-${i}`}>
                    <TableCell><Input className="h-8 w-32" defaultValue={s.label} onChange={(e) => (s.label = e.target.value)} /></TableCell>
                    <TableCell><Input className="h-8 w-20" defaultValue={s.min_orders} onChange={(e) => (s.min_orders = e.target.value)} /></TableCell>
                    <TableCell><Input className="h-8 w-20" defaultValue={s.max_orders ?? ""} placeholder="∞" onChange={(e) => (s.max_orders = e.target.value)} /></TableCell>
                    <TableCell><Input className="h-8 w-24" defaultValue={s.rate} onChange={(e) => (s.rate = e.target.value)} data-testid={`fslab-rate-${i}`} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={async () => { try { await api.put(`/franchise-slabs/${s.id}`, { label: s.label, min_orders: Number(s.min_orders), max_orders: s.max_orders === "" || s.max_orders === null ? null : Number(s.max_orders), rate: Number(s.rate) }); toast.success("Saved"); load(); } catch (e) { toast.error("Failed"); } }} data-testid={`save-fslab-${i}`}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={async () => { try { await api.delete(`/franchise-slabs/${s.id}`); load(); } catch (e) { toast.error("Failed"); } }}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card" data-testid="add-slab-dialog">
          <DialogHeader><DialogTitle className="font-head">Add Earning Slab</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Label</Label><Input value={nf.label || ""} onChange={(e) => setNf({ ...nf, label: e.target.value })} placeholder="e.g. 45+ Orders" /></div>
            <div><Label>Min Orders</Label><Input type="number" value={nf.min_orders || ""} onChange={(e) => setNf({ ...nf, min_orders: e.target.value })} /></div>
            <div><Label>Max Orders (blank=∞)</Label><Input type="number" value={nf.max_orders || ""} onChange={(e) => setNf({ ...nf, max_orders: e.target.value })} /></div>
            <div><Label>Rate (₹/order)</Label><Input type="number" value={nf.rate || ""} onChange={(e) => setNf({ ...nf, rate: e.target.value })} /></div>
            <div><Label>Platform</Label><Select value={nf.platform} onValueChange={(v) => setNf({ ...nf, platform: v })}><SelectTrigger><SelectValue placeholder="All" /></SelectTrigger><SelectContent>{["All", "Zomato", "Swiggy", "Blinkit", "Zepto"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={addSlab} data-testid="save-slab-dialog">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
